/**
 * Customer catalog reads — deployed area-filtered endpoints only:
 *   GET /services-catageory?area_city=...
 *   GET /services?area_city=...
 *
 * Do not use findAllServiceCatageory or fetchAllServices here (admin only).
 */
import apiClient from './apiClient';
import {
  CUSTOMER_CATEGORIES_ENDPOINT,
  CUSTOMER_SERVICES_ENDPOINT,
} from './deployedContracts';
import type { Category, Service } from '../types/Login';
import { normalizeKnownServiceArea } from '../utils/serviceArea';

const ARRAY_KEYS = ['data', 'items', 'categories', 'services', 'payload', 'records', 'result', 'rows'];

export function areaCityCandidates(areaCity?: string): string[] {
  const trimmed = areaCity?.trim();
  if (!trimmed) return [];
  const primary = normalizeKnownServiceArea(trimmed);
  if (primary.toLowerCase() !== trimmed.toLowerCase()) {
    return [trimmed, primary];
  }
  return [trimmed];
}

async function fetchServicesByCategoryId(categoryId: string): Promise<Service[]> {
  try {
    const response = await apiClient.get(`/services/fetchByService_Catageory/${categoryId}`);
    if (isErrorEnvelope(response.data)) return [];
    return extractServicesFromBody(response.data);
  } catch {
    return [];
  }
}

function isErrorEnvelope(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const o = data as Record<string, unknown>;
  if (o.success === false) return true;
  if (typeof o.statusCode === 'number' && o.statusCode >= 400) return true;
  return false;
}

function parseArrayResponse<T>(data: unknown, keys: string[] = ARRAY_KEYS): T[] {
  if (Array.isArray(data)) return data as T[];
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  for (const key of keys) {
    const val = o[key];
    if (Array.isArray(val)) return val as T[];
    if (val && typeof val === 'object' && Array.isArray((val as Record<string, unknown>).data)) {
      return (val as { data: T[] }).data;
    }
  }
  return [];
}

function extractCategoriesFromBody(responseData: unknown): Category[] {
  const body = responseData as Record<string, unknown> | null;
  const raw = body?.data !== undefined ? body.data : responseData;
  const arr = parseArrayResponse<Category>(raw, ARRAY_KEYS);
  if (arr.length > 0) return arr;

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const v of Object.values(raw as Record<string, unknown>)) {
      if (Array.isArray(v) && v.length > 0) {
        const first = v[0];
        if (first && typeof first === 'object' && ('service_catageory_name' in first || 'id' in first || 'name' in first)) {
          return v as Category[];
        }
      }
    }
  }
  return [];
}

function extractServicesFromBody(responseData: unknown): Service[] {
  const body = responseData as Record<string, unknown> | null;
  const raw = body?.data !== undefined ? body.data : responseData;
  return parseArrayResponse<Service>(raw, ['data', 'services', 'items']);
}

/** GET /services-catageory?area_city=… (tries alias cities e.g. Bhadravathi → Shivamogga). */
export async function fetchCustomerCategories(areaCity?: string): Promise<Category[]> {
  const candidates = areaCityCandidates(areaCity);
  if (candidates.length === 0) return [];

  for (const candidate of candidates) {
    try {
      const response = await apiClient.get(CUSTOMER_CATEGORIES_ENDPOINT, {
        params: { area_city: candidate },
      });
      if (isErrorEnvelope(response.data)) continue;
      const list = extractCategoriesFromBody(response.data);
      if (list.length > 0) return list;
    } catch {
      continue;
    }
  }
  return [];
}

/**
 * GET /services?area_city=… plus every service under each category in this area.
 * Location-based /services alone is often partial (only rows in `location` table);
 * category fetch ensures "All services" shows the full catalog for the area.
 */
export async function fetchCustomerServices(areaCity?: string): Promise<Service[]> {
  const candidates = areaCityCandidates(areaCity);
  if (candidates.length === 0) return [];

  const byId = new Map<string, Service>();

  for (const candidate of candidates) {
    try {
      const response = await apiClient.get(CUSTOMER_SERVICES_ENDPOINT, {
        params: { area_city: candidate },
      });
      if (isErrorEnvelope(response.data)) continue;
      const list = extractServicesFromBody(response.data);
      for (const svc of list) {
        const sid = svc?.id != null ? String(svc.id) : '';
        if (sid) byId.set(sid, svc);
      }
    } catch {
      continue;
    }
  }

  const categories = await fetchCustomerCategories(areaCity);
  await Promise.all(
    categories.map(async (cat) => {
      const id = cat?.id != null ? String(cat.id) : '';
      if (!id) return;
      const list = await fetchServicesByCategoryId(id);
      for (const svc of list) {
        const sid = svc?.id != null ? String(svc.id) : '';
        if (sid) byId.set(sid, svc);
      }
    }),
  );

  return Array.from(byId.values());
}
